import useAuth from '@State/Auth/useAuth';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import Avatar from '../Avatar';
import { SidebarCtx } from './SidebarContext';

const Wrapper = styled.div`
  position: relative;
  overflow: visible;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const ProfileAvatar = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ProfileText = styled.div`
  text-align: left;
  color: ${(props) => props.theme.color.text.main};
`;

const ProfileName = styled.div`
  font-weight: 700;
  font-size: 15px;
  color: ${(props) => props.theme.color.text.main};
  margin-bottom: 4px;
`;

const ProfileEmail = styled.div`
  font-size: 13px;
  color: ${(props) => props.theme.color.text.alt};
  font-weight: 400;
  margin-top: 2px;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownMenu = styled.div`
  position: absolute;
  left: 0;
  bottom: 100%;
  min-width: 180px;
  background-color: ${(props) => props.theme.color.bg.alt};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 12px;
`;

const menuItemBase = css`
  padding: 10px 18px;
  background: none;
  border: none;
  color: ${(props) => props.theme.color.text.main};
  font-family: inherit;
  font-weight: 500;
  font-size: 14px;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
  outline: none;
  appearance: none;
  display: block;

  &:hover,
  &:focus {
    background: ${(props) => props.theme.color.bg.main};
  }
`;

const MenuItemLink = styled(Link)`
  ${menuItemBase}
`;

const MenuItemButton = styled.button`
  ${menuItemBase}
`;

const SidebarProfile: React.FC = () => {
  const { t } = useTranslation();
  const { parsedToken, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { showMobileMenu, toggleMobileMenu } = useContext(SidebarCtx);

  const userInitials = parsedToken
    ? `${parsedToken?.given_name.charAt(0)}${parsedToken?.family_name.charAt(0)}`
    : '';

  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <Wrapper ref={ref}>
      <ProfileButton
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        title={t('toggle_profile')}
      >
        <ProfileAvatar>
          <Avatar initials={userInitials} />
        </ProfileAvatar>
        <ProfileText>
          <ProfileName>
            {parsedToken?.given_name} {parsedToken?.family_name}
          </ProfileName>
          <ProfileEmail>{parsedToken?.email}</ProfileEmail>
        </ProfileText>
      </ProfileButton>
      {open && (
        <DropdownMenu title={t('header_profile_panel_title')}>
          <MenuItemLink
            to="/admin/my-pages"
            title={t('my_pages')}
            onClick={() => {
              setOpen(false);
              if (showMobileMenu) {
                toggleMobileMenu();
              }
            }}
          >
            {t('my_pages', 'Mina sidor')}
          </MenuItemLink>
          <MenuItemLink
            to="/privacy-policy"
            title={t('footer_link_privacy_policy_text')}
            onClick={() => {
              setOpen(false);
              if (showMobileMenu) {
                toggleMobileMenu();
              }
            }}
          >
            {t('footer_link_privacy_policy_text')}
          </MenuItemLink>
          <MenuItemLink
            to="/terms-of-use"
            title={t('termsofuse_title')}
            onClick={() => {
              setOpen(false);
              if (showMobileMenu) {
                toggleMobileMenu();
              }
            }}
          >
            {t('termsofuse_title')}
          </MenuItemLink>
          <MenuItemButton title={t('header_profile_panel_signout_btn_title')} onClick={signOut}>
            {t('logout', 'Logga ut')}
          </MenuItemButton>
        </DropdownMenu>
      )}
    </Wrapper>
  );
};

export default SidebarProfile;
